import { JourneyCanvasState, JourneyStepAction, SidebarState, StepJourneyDataMap } from '../../utility/TypeDefs';
import { FetchAllServicesAndJourneyDataPayload, LoadJourneyMetadataPayload, SaveJourneyPayload, SaveJourneySuccessPayload, SetEventSchemaPayload, ViewEditJourneyPayload } from '../TypeDefs';

/**
 * @method fetchAllServicesAndJourneyData
 * @description assign all services and journey data fetched from view journey api
 * @param state Journey canvas state
 * @param compDataMap Compensatory data map
 * @param connectors connectors
 * @param isRootExternal if root is external
 * @param itemDataMap flow item data map
 * @param items flow items
 * @param rootNodeId node id of first node
 * @param services get all services
 * @returns All services and journey data
 */
export const fetchAllServicesAndJourneyData = (state: JourneyCanvasState, {
  connectors,
  stepJourneyDataMap,
  flowItemDataMap,
  items,
  services,
  payloadTransformations,
  isCreate
}: FetchAllServicesAndJourneyDataPayload): JourneyCanvasState => ({
  ...state,
  stepItems: items,
  stepConnectors: connectors,
  stepJourneyDataMap,
  flowItemDataMap,
  allServices: services ?? [],
  enableActiveJourney: state.journeyStatus.toLowerCase() === 'draft' && items.length > 0,
  initiateStepCardVisible: items.length === 0,
  createdJourney: items.length > 0,
  payloadTransformations,
  designingStep: false,
  designingCEM: false,
  isJourneyCreated: !isCreate
});

/**
 * @method saveJourney
 * @description set save state to saveJourney
 * @param state Journey canvas state
 * @param save save journey button state if enable/disable
 * @returns saveJourney
 */
export const saveJourneyBtnEnable = (state: JourneyCanvasState, {
  save
}: SaveJourneyPayload): JourneyCanvasState => ({
  ...state,
  saveJourneyBtnEnabled: save
});

/**
 * @method selectStart
 * @description sidebar should be visible when start is selected
 * @param state Journey canvas state
 * @returns startSelected and sidebarState
 */
export const selectStart = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  startSelected: true,
  sidebarState: state.flowItems.length === 0 ? SidebarState.ALL_SERVICES : SidebarState.INGRESS_EVENTS,
});

/**
 * @method loadJourneyMetadata
 * @description get journey meta data
 * @param state Journey canvas state
 * @param name journey name
 * @param status journey status
 * @param uuid journey UUID
 * @param description journey description
 * @param maxExecTime maximum execution time
 * @param uniqueIdentifier journey uniqueIdentifier
 * @returns Journey data
 */
export const loadJourneyMetadata = (state: JourneyCanvasState, {
  name,
  status,
  description,
  version,
  category,
  type,
  uuid,
  comments
}: LoadJourneyMetadataPayload): JourneyCanvasState => ({
  ...state,
  journeyName: name,
  journeyStatus: status,
  journeyDescription: description,
  journeyVersion: version,
  journeyCategory: category,
  journeyType: type,
  journeyUuid: uuid,
  journeyComments: comments,
});

/**
 * @method saveJourneySuccess
 * @description changes status if journey is saved successfully
 * @param state Journey canvas state
 * @param status status of journey
 * @returns journeyStatus and enableActiveJourney
 */
export const saveJourneySuccess = (state: JourneyCanvasState, {
  uuid,
  steps,
  version,
  status,
}: SaveJourneySuccessPayload): JourneyCanvasState => {
  const stepJourneyDataMap: StepJourneyDataMap = {};
  const stepNodeIds = state.stepItems.map((si) => si.nodeId);
  Object.keys(state.stepJourneyDataMap).forEach((nodeId) => {
    if (stepNodeIds.includes(nodeId)) {
      // filter out deleted steps, update journeyStepAction and eventMapName
      state.stepJourneyDataMap[nodeId].journeyStepAction = JourneyStepAction.NONE;
      state.stepJourneyDataMap[nodeId].eventMapName = steps[state.stepJourneyDataMap[nodeId].journeyStepName].eventMapName;
      stepJourneyDataMap[nodeId] = state.stepJourneyDataMap[nodeId];
    }
  });
  Object.values(state.flowItemDataMap).forEach((i) => { i.isSaved = true; });
  return {
    ...state,
    createdJourney: true,
    saveJourneyBtnEnabled: false,
    deletedStepItems: [],
    deletedStepConnectors: [],
    stepJourneyDataMap,
    flowItemDataMap: { ...state.flowItemDataMap },
    isJourneyCreated: true,
    journeyUuid: uuid,
    journeyVersion: version,
    journeyStatus: status
  };
};

/**
 * @method setViewJourneyInitiatedFlag
 * @description Sets the view journey initiated flag.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {ViewEditJourneyPayload} payload - The payload containing the flag.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const setViewJourneyInitiatedFlag = (state: JourneyCanvasState, { flag }: ViewEditJourneyPayload): JourneyCanvasState => ({
  ...state,
  viewJourneyInitiated: flag,
});

/**
 * @method showSaveJourneyModal
 * @description Toggles the visibility of the save journey modal.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const showSaveJourneyModal = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  saveJourneyToDesignCEMModal: !state.saveJourneyToDesignCEMModal
});

export const setEventSchema = (state: JourneyCanvasState, { key, eventSchema }: SetEventSchemaPayload): JourneyCanvasState => ({
  ...state,
  eventSchemaMap: { ...state.eventSchemaMap, [key]: eventSchema },
  selectedEventSchema: eventSchema,
});

export const clearEventSchema = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  selectedEventSchema: undefined,
});
